import { createContext, useEffect, useReducer } from 'react';
import type { FC, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import KeycloakServices from '../Keycloak-config';

import { authenticateUser } from '../api/requests/authenticateUser';
import { getAllClients, getClientById } from '../api/requests/clients';
import { getClientUser, updateUserActivityForLogin } from '../api/requests/user';
import { getAllAccessRoles } from '../api/requests/accessRole';
import { getFeatures, getFeaturesByClientId } from '../api/requests/features';

import { Role, User } from '../types/user';
import { adminRoles, Roles, Routes, StorageKeys, Tokens, UserActivityTypes } from '../constants';
import { getDarkColor } from '../utils/utils';

interface State {
  user: User | null;
  permissions: string[];
  isLoading: boolean;
  isAuthenticated: boolean;
  clientUsers: User[];
  roles: Role[];
  clientList: any[];
  userRole: string;
  userClient: any;
  clientId: string | null;
  changeClientId: (val: string | null, drop?: boolean, userData?: any) => void;
  isDropdownClicked: boolean;
  globalFeatures: any[];
  productsAccess: string[];
  isReadOnly: boolean;
  changeUserRole: (val: string, userData?: any) => void
  haveAllAccess: boolean
}

interface AuthProviderProps {
  children?: ReactNode;
}

const initialState: State = {
  user: null,
  permissions: [],
  clientUsers: [],
  roles: [],
  isLoading: true,
  isAuthenticated: false,
  clientList: [],
  userRole: '',
  userClient: null,
  clientId: null,
  changeClientId: (val: string | null, drop?: boolean, userData?: any) => { },
  isDropdownClicked: false,
  globalFeatures: [],
  productsAccess: [],
  isReadOnly: false,
  changeUserRole: (val: string, userData?: any) => { },
  haveAllAccess: false
};

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    user: User | null;
    permissions: string[];
    isLoading: boolean;
    isAuthenticated: boolean;
    clientUsers: User[];
    roles: Role[];
    clientList: any[];
    userRole: string;
    userClient: any;
    clientId: string | null;
    changeClientId: (val: string | null, drop?: boolean, userData?: any) => void;
    isDropdownClicked: boolean;
    globalFeatures: any[];
    productsAccess: string[];
    isReadOnly: boolean;
    changeUserRole: (val: string, userData?: any) => void;
    haveAllAccess: boolean;
  };
};

type Action = InitializeAction;

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const {
      user,
      permissions,
      isLoading,
      isAuthenticated,
      clientUsers,
      roles,
      clientList,
      userRole,
      userClient,
      clientId,
      changeClientId,
      isDropdownClicked,
      globalFeatures,
      productsAccess,
      isReadOnly,
      changeUserRole,
      haveAllAccess
    } = action.payload;

    return {
      ...state,
      isAuthenticated,
      user,
      permissions,
      isLoading,
      clientUsers,
      roles,
      clientList,
      userRole,
      userClient,
      clientId,
      changeClientId,
      isDropdownClicked,
      globalFeatures,
      productsAccess,
      isReadOnly,
      changeUserRole,
      haveAllAccess
    };
  },
};

const reducer = (state: State, action: Action): State => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext<State>({
  ...initialState
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const navigate = useNavigate();
  const localClientId = localStorage.getItem(StorageKeys.clientId);
  const [state, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();
  const dashboardUrl = [Routes.person, Routes.truck, Routes.transit, Routes.personPlus, Routes.perform];

  const isDashboardUrl = dashboardUrl?.find((item: any) => {
    const isUnrestricted = [Routes.performanceReport].find((route) => location.pathname.includes(route));
    if (isUnrestricted) {
      return false;
    }
    return location?.pathname?.includes(item);
  });

  const onFetchingDetails = async (clientId: string | null, activeRole: string, userInfo?: any) => {
    let isReadOnly: boolean = false;
    let selectedClientId = clientId;
    let permissions: any = [];
    let clientUserDetails: any;
    let userRole: string = activeRole ?? '';
    let userClient: any;
    let clientList = [];
    let productsAccess: string[] = [];
    let roles: any[] = [];
    const featuresList: any[] = [];
    const getLocalClientId = localStorage.getItem(StorageKeys.clientId);
    const user: any = userInfo;
    const userSingleClientId = user?.client?.[0]?.id;

    // For checking the user is Site Admin
    const isSiteAdmin = adminRoles?.includes(userRole as any);

    if (!isSiteAdmin && (user?.client?.length === 1)) {
      selectedClientId = userSingleClientId;
    }

    if (!localStorage.getItem(StorageKeys.loggedIn) && user && (selectedClientId || isSiteAdmin)) {
      // For updating user activity
      const updateUserDetails = await updateUserActivityForLogin(UserActivityTypes.Login, selectedClientId ?? '');
      localStorage.setItem(StorageKeys.loggedIn, 'true');
    }

    if (isSiteAdmin) {
      // For setting user role if site admin

      if (selectedClientId) {
        // For fetching client list if site admin
        // For fetching features for a clients
        const [clientDetails, features, clientDetailsById] = await Promise.all([
          !isDashboardUrl && getAllClients(true),
          getFeaturesByClientId(selectedClientId ?? userSingleClientId ?? ''),
          getClientById(selectedClientId, Boolean(isDashboardUrl))
        ]);

        featuresList.push(...features?.data);
        clientList = clientDetails?.data;

        // For setting user client by selected client id if site admin
        userClient = clientDetailsById?.data;
        productsAccess = userClient?.clientproducts?.map((item: any) => item?.product?.name);
      } else {
        // For fetching client list if site admin
        // For fetching all features

        const [clientDetails, features] = await Promise.all([
          !isDashboardUrl && getAllClients(true),
          !isDashboardUrl && getFeatures()
        ]);

        features?.data?.forEach((feature: any) => {
          const featuresDetails = feature?.features?.map((item: any) => ({
            ...item,
            id: feature?.id
          }));
          featuresList.push(...featuresDetails);
        });

        clientList = clientDetails?.data;
        // For setting user client by selected client id if site admin
        userClient = null;
      }
    } else {
      // For other roles except site admin

      userClient = user?.client?.find((client: any) => (
        selectedClientId ? (client?.id === selectedClientId) : (client?.id === userSingleClientId)));

      if (userClient) {
        userRole = userClient?.role?.[0]?.name;
        localStorage.setItem(StorageKeys.userRole, userRole);

        permissions = userClient?.role?.[0]?.permissions;
        productsAccess = userClient?.clientproducts?.map((item: any) => item?.product?.name);
        // For fetching users of a particular client if not site admin
        // For fetching features for a clients

        const [features, userClientDetails]: any = localStorage.getItem(Tokens.accessToken) && await Promise.all([
          !isDashboardUrl && getFeaturesByClientId(selectedClientId ?? userSingleClientId ?? ''),
          getClientUser(selectedClientId ?? userSingleClientId)
        ]);

        features?.data?.length && featuresList.push(...features?.data);
        clientUserDetails = userClientDetails;
      } else {
        selectedClientId = null;
      }
    }

    const clientUsers: any = clientUserDetails?.data?.rows?.length > 0 ? (
      clientUserDetails?.data?.rows?.map((item: any) => ({
        ...item,
        backgroundColor: getDarkColor()
      }))
    ) : [];

    // For fetching access roles
    if (localStorage.getItem(Tokens.accessToken)) {
      const rolesFetched = await getAllAccessRoles();
      roles = rolesFetched?.data?.filter((item: any) => item.name !== 'Owner') ?? [];
    }

    // For setting global client id
    if ((selectedClientId === 'all') || !selectedClientId) {
      localStorage.removeItem(StorageKeys.clientId);
    } else {
      localStorage.setItem(StorageKeys.clientId, selectedClientId ?? '');
    }

    if (localStorage.getItem(Tokens.accessToken)) {
      userRole && localStorage.setItem(StorageKeys.userRole, userRole);
    }
    // For navigating to home when client change

    if (getLocalClientId !== clientId) {
      const firstSegment = `/${window.location.pathname.split('/').filter(Boolean)[0]}`;
      if (window.location.pathname !== firstSegment) {
        navigate(firstSegment);
      }
    }

    if (userRole === Roles.SITE_ADMIN_R) {
      isReadOnly = true;
    }

    return {
      isReadOnly,
      clientId: selectedClientId,
      user,
      permissions,
      clientUsers,
      userRole,
      userClient,
      clientList,
      productsAccess,
      globalFeatures: featuresList,
      roles
    };
  };

  const changeUserRole = async (role: string, userData?: any) => {
    localStorage.setItem(StorageKeys.userRole, role);
    const details: any = await onFetchingDetails(localStorage.getItem(StorageKeys.clientId), role, userData?.user);
    dispatch({
      type: 'INITIALIZE',
      payload: {
        ...userData,
        ...details,
        haveAllAccess: adminRoles?.includes(role as any)
      }
    });
  };

  const initialize = debounce(async (clientId: string | null, isDropdownClicked?: boolean, userInfo?: any): Promise<void> => {
    try {
      let user: any = userInfo;
      let userRole: any = localStorage.getItem(StorageKeys.userRole) ?? '';

      if (!userInfo) {
        // For fetching user details
        const userDetails = localStorage.getItem(Tokens.accessToken) && await authenticateUser();
        user = userDetails?.data?.user;
      }

      if (!userRole) {
        const haveAccess = user?.role?.find((role: any) => adminRoles?.includes(role?.name));

        if (user?.client?.length > 0) {
          userRole = user?.role?.find((item: any) => !adminRoles?.includes(item?.name))?.name;
        } else {
          userRole = haveAccess?.name;
        }
      }

      const details: any = await onFetchingDetails(clientId, userRole, user);

      dispatch({
        type: 'INITIALIZE',
        payload: {
          ...details,
          isLoading: false,
          isAuthenticated: true,
          changeClientId: initialize,
          isDropdownClicked: isDropdownClicked ?? false,
          changeUserRole,
          haveAllAccess: adminRoles?.includes(userRole as any)
        }
      });
    } catch (err: any) {
      dispatch({
        type: 'INITIALIZE',
        payload: {
          user: null,
          permissions: [],
          isAuthenticated: false,
          isLoading: false,
          clientUsers: [],
          roles: [],
          clientList: [],
          userRole: '',
          userClient: null,
          clientId: '',
          changeClientId: initialize,
          isDropdownClicked: false,
          globalFeatures: [],
          productsAccess: [],
          isReadOnly: false,
          changeUserRole: (val: string) => { },
          haveAllAccess: false
        }
      });
    }
  }, 500);

  useEffect(() => {
    localStorage.removeItem(StorageKeys.redirect);
    KeycloakServices.initKeycloak(() => initialize(localClientId));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
